import { motion } from "framer-motion";
import { Reveal, Counter } from "./primitives";
import { ASSETS } from "@/data/content";
import { TrendingUp, Target, Workflow, ArrowRight, Image as ImageIcon } from "lucide-react";

const SectionHead = () => (
  <div className="max-w-7xl mx-auto px-6 md:px-12">
    <Reveal>
      <span className="text-xs tracking-[0.22em] uppercase font-bold text-[#e24a29]">Selected Work</span>
    </Reveal>
    <Reveal delay={0.1}>
      <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight leading-[0.95] max-w-4xl">
        Case studies, built around outcomes.
      </h2>
    </Reveal>
  </div>
);

const Tag = ({ children }) => (
  <span className="text-[11px] tracking-[0.14em] uppercase font-bold px-3 py-1 rounded-full border border-[rgba(28,27,26,0.2)] text-[#57534e]">
    {children}
  </span>
);

const BigMetric = ({ value, prefix, suffix, label, decimals }) => (
  <div>
    <div className="font-display font-black text-5xl md:text-6xl tracking-tight leading-none">
      <Counter end={value} prefix={prefix} suffix={suffix} decimals={decimals} />
    </div>
    <div className="mt-2 text-sm text-[#57534e]">{label}</div>
  </div>
);

/* 01 — YouTube growth */
const YouTubeStudy = () => (
  <div className="max-w-7xl mx-auto px-6 md:px-12">
    <Reveal>
      <div className="rounded-3xl border border-[rgba(28,27,26,0.12)] bg-[#fffdf9] overflow-hidden">
        <div className="grid lg:grid-cols-2">
          <div className="p-8 md:p-12 flex flex-col">
            <div className="flex items-center gap-2 text-[#e24a29] mb-6">
              <TrendingUp size={18} />
              <span className="font-display text-lg font-bold">01</span>
            </div>
            <div className="flex flex-wrap gap-2 mb-6">
              <Tag>Social &amp; Content</Tag>
              <Tag>Performance</Tag>
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-tight">
              Scaling a YouTube channel from 40K to 60K+ in 4 months
            </h3>
            <p className="mt-5 text-[#57534e] leading-relaxed">
              A blended paid + organic play. I ran a full content audit, doubled down on what was already working, and
              layered paid promotion on the winners. The result compounded across watch-time, reach and subs.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-[#57534e]">
              <li className="flex gap-2"><span className="text-[#e24a29]">—</span> Content audit to find repeatable winning formats</li>
              <li className="flex gap-2"><span className="text-[#e24a29]">—</span> Paid promotion layered onto proven organic hits</li>
              <li className="flex gap-2"><span className="text-[#e24a29]">—</span> Key learning: distribution beats volume</li>
            </ul>
            <div className="mt-auto pt-10 grid grid-cols-2 gap-6">
              <BigMetric value={60} suffix="K+" label="Subscribers reached" />
              <BigMetric value={20} prefix="+" suffix="K" label="Gained in 4 months" />
            </div>
          </div>
          <div className="relative bg-[#101010] p-4 md:p-6 flex items-center">
            <span className="absolute top-6 left-6 z-10 text-[11px] tracking-[0.14em] uppercase font-bold text-white/60">
              Real analytics · work sample
            </span>
            <motion.img
              src={ASSETS.youtubeAnalytics}
              alt="YouTube channel analytics showing view and subscriber growth"
              className="w-full rounded-xl shadow-2xl"
              initial={{ scale: 1.05 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </Reveal>
  </div>
);

/* 02 — Meta Ads internship */
const META_STATS = ["CTR", "CPC", "CPM", "ROAS"];
const MetaStudy = () => (
  <div className="max-w-7xl mx-auto px-6 md:px-12">
    <Reveal>
      <div className="rounded-3xl border border-[rgba(28,27,26,0.12)] bg-[#101010] text-[#f5f3ef] p-8 md:p-14">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12">
          <div>
            <div className="flex items-center gap-2 text-[#e24a29] mb-6">
              <Target size={18} />
              <span className="font-display text-lg font-bold">02</span>
            </div>
            <div className="flex flex-wrap gap-2 mb-6">
              <Tag>
                <span className="text-[#a8a29e]">Performance</span>
              </Tag>
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-tight">
              Performance Marketing Internship — Grah Shakti Mahavastu
            </h3>
            <p className="mt-5 text-[#a8a29e] leading-relaxed max-w-lg">
              Owned the paid-media stack end to end: Meta Ads campaign setup, audience targeting, and a clean Pixel /
              CAPI tracking foundation — reported against the metrics that matter, every week.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-[#d6d3d1]">
              <li className="flex gap-2"><span className="text-[#e24a29]">—</span> Meta Ads Manager setup &amp; structure</li>
              <li className="flex gap-2"><span className="text-[#e24a29]">—</span> Interest &amp; lookalike audience targeting</li>
              <li className="flex gap-2"><span className="text-[#e24a29]">—</span> Pixel + Conversions API (CAPI) tracking</li>
              <li className="flex gap-2"><span className="text-[#e24a29]">—</span> Weekly performance reporting</li>
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-4 self-center">
            {META_STATS.map((s, i) => (
              <motion.div
                key={s}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                className="aspect-square rounded-2xl border border-white/12 bg-white/[0.03] flex flex-col items-center justify-center hover:border-[#e24a29]/60 transition-colors duration-300"
              >
                <span className="font-display text-4xl md:text-5xl font-black text-[#e24a29]">{s}</span>
                <span className="mt-2 text-xs tracking-[0.15em] uppercase text-[#a8a29e]">tracked weekly</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </Reveal>
  </div>
);

/* 03 — n8n automation */
const FLOW = ["n8n Trigger", "Gemini 2.5 Flash", "Google Sheets", "Slack Alert"];
const AutomationStudy = () => (
  <div className="max-w-7xl mx-auto px-6 md:px-12">
    <Reveal>
      <div className="rounded-3xl border border-[rgba(28,27,26,0.12)] bg-[#fffdf9] p-8 md:p-14">
        <div className="flex items-center gap-2 text-[#e24a29] mb-6">
          <Workflow size={18} />
          <span className="font-display text-lg font-bold">03</span>
        </div>
        <div className="flex flex-wrap gap-2 mb-6">
          <Tag>GTM &amp; Growth Systems</Tag>
        </div>
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12 items-start">
          <div>
            <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-tight">
              AI-powered B2B prospecting &amp; outreach automation
            </h3>
            <p className="mt-5 text-[#57534e] leading-relaxed max-w-lg">
              A hands-off growth system: n8n orchestrates enrichment, Gemini 2.5 Flash scores fit, results land in
              Sheets and high-fit accounts ping Slack. What used to be a day of manual research now runs in minutes.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6">
              <BigMetric value={50} label="Companies / run" />
              <BigMetric value={16} suffix="%" label="High-fit rate" />
              <BigMetric value={6} prefix="<" suffix=" min" label="Turnaround" />
            </div>
          </div>
          {/* mini node flow */}
          <div className="space-y-3">
            {FLOW.map((node, i) => (
              <motion.div
                key={node}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.5 }}
              >
                <div className="flex items-center justify-between px-5 py-4 rounded-xl border border-[rgba(28,27,26,0.15)] bg-[#f5f3ef] font-semibold text-sm">
                  <span>{node}</span>
                  <span className="font-display text-[#e24a29]">{String(i + 1).padStart(2, "0")}</span>
                </div>
                {i < FLOW.length - 1 && (
                  <div className="flex justify-center py-1 text-[#e24a29]">
                    <ArrowRight size={16} className="rotate-90" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </Reveal>
  </div>
);

/* 04 — Thumbnail gallery */
const ThumbnailStudy = () => (
  <div className="max-w-7xl mx-auto px-6 md:px-12">
    <Reveal>
      <div className="flex items-center gap-2 text-[#e24a29] mb-6">
        <ImageIcon size={18} />
        <span className="font-display text-lg font-bold">04</span>
      </div>
      <div className="flex flex-wrap items-end justify-between gap-6 mb-4">
        <div>
          <div className="flex flex-wrap gap-2 mb-4">
            <Tag>Social &amp; Content</Tag>
          </div>
          <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-tight max-w-xl">
            Thumbnail design for a 150K+ Instagram influencer
          </h3>
        </div>
        <p className="text-[#57534e] max-w-sm">
          Scroll-stopping, high-contrast thumbnails built to earn the click. Bold type, clear hierarchy, on-brand every
          time.
        </p>
      </div>
    </Reveal>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
      {ASSETS.thumbnails.map((src, i) => (
        <Reveal key={src} delay={i * 0.08}>
          <div className="group relative overflow-hidden rounded-2xl border border-[rgba(28,27,26,0.12)] bg-[#fffdf9]">
            <img
              src={src}
              alt={`Instagram thumbnail design sample ${i + 1}`}
              data-testid={`thumbnail-${i}`}
              className="w-full h-full object-cover aspect-[4/5] group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          </div>
        </Reveal>
      ))}
    </div>
  </div>
);

export const CaseStudies = () => (
  <section id="work" data-testid="work-section" className="py-24 md:py-36">
    <SectionHead />
    <div className="mt-16 md:mt-24 space-y-16 md:space-y-24">
      <YouTubeStudy />
      <MetaStudy />
      <AutomationStudy />
      <ThumbnailStudy />
    </div>
  </section>
);
